<template>
  <div class="d-flex flex-column" :class="{ borderless: !compact }" data-test-id="builder-step">
    <div
      class="d-flex flex-row align-center"
      :class="{ 'cursor-pointer': isRowClickable }"
      v-bind="rowAriaAttrs"
      @click="handleStepClick"
      @keydown.enter.prevent="handleStepClick"
      @keydown.space.prevent="handleStepClick"
    >
      <Icon
        :icon="icon"
        tile
        aria-hidden="true"
        :class="compact && substeps.length ? 'align-self-start' : ''"
        :color="error ? 'error' : active || completed ? 'systemPrimary' : 'grey'"
      />
      <div :class="['step-content', active && !substeps.length ? 'active' : '']">
        <div class="step-label-row">
          <Icon
            v-if="error"
            icon="mdi-alert-circle-outline"
            small
            color="error"
            aria-hidden="true"
          />
          <Typography
            :class="['text-left step-label', error && 'error--text']"
            :variant="
              (active && !substeps.length) || (active && parentClickableWithSubsteps)
                ? 'subtitle-1'
                : 'body-1'
            "
          >
            {{ label }}
          </Typography>
        </div>
        <div v-if="compact && substeps && substeps.length" class="substeps">
          <div
            v-for="(substep, index) in substeps"
            :key="substep.key ?? index"
            class="substep-row"
            :class="{ 'cursor-pointer': isSubstepClickable(substep, index) }"
            v-bind="substepAriaAttrs(substep, index)"
            @click.stop="handleSubstepClick(substep, index)"
            @keydown.enter.prevent.stop="handleSubstepClick(substep, index)"
            @keydown.space.prevent.stop="handleSubstepClick(substep, index)"
          >
            <Icon
              :icon="substep.icon"
              tile
              small
              aria-hidden="true"
              :color="
                isSubstepActive(substep) || isSubstepCompleted(substep, index)
                  ? 'systemPrimary'
                  : 'grey'
              "
            />
            <div :class="['substep-label', isSubstepActive(substep) ? 'active' : '']">
              <Typography
                :variant="isSubstepActive(substep) ? 'subtitle-1' : 'body-2'"
                class="text-left substep-label-text"
                >{{ substep.label }}</Typography
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!compact && substeps && substeps.length" class="substeps sidebar-substeps">
      <div
        v-for="(substep, index) in substeps"
        :key="substep.key ?? index"
        class="substep-row"
        :class="{ 'cursor-pointer': isSubstepClickable(substep, index) }"
        v-bind="substepAriaAttrs(substep, index)"
        @click.stop="handleSubstepClick(substep, index)"
        @keydown.enter.prevent.stop="handleSubstepClick(substep, index)"
        @keydown.space.prevent.stop="handleSubstepClick(substep, index)"
      >
        <div :class="['substep-label', isSubstepActive(substep) ? 'active' : '']">
          <Typography
            :variant="isSubstepActive(substep) ? 'subtitle-1' : 'body-2'"
            class="text-left substep-label-text"
            >{{ substep.label }}</Typography
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import Icon from "@/components/generic/Icon.vue";

export default {
  name: "BuilderStep",
  components: {
    Typography,
    Icon,
  },
  props: {
    icon: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
    completed: {
      type: Boolean,
      default: false,
    },
    substeps: {
      type: Array,
      default: () => [],
    },
    currentSubstepKey: {
      type: String,
      default: null,
    },
    currentSubstepIndex: {
      type: Number,
      default: -1,
    },
    clickable: {
      type: Boolean,
      default: false,
    },
    highestSubstepReached: {
      type: Number,
      default: -1,
    },
    compact: {
      type: Boolean,
      default: true,
    },
    freeNavigation: {
      type: Boolean,
      default: false,
    },
    parentClickableWithSubsteps: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["click-step", "click-substep"],
  computed: {
    isRowClickable() {
      if (this.loading) return false;
      if (this.parentClickableWithSubsteps) return this.clickable;
      return this.clickable && !this.substeps.length;
    },
    rowAriaAttrs() {
      if (!this.isRowClickable) return {};
      return {
        role: "button",
        tabindex: 0,
        "aria-label": this.label,
      };
    },
  },
  methods: {
    substepAriaAttrs(substep, index) {
      if (!this.isSubstepClickable(substep, index)) return {};
      return {
        role: "button",
        tabindex: 0,
        "aria-label": substep.label,
      };
    },
    isSubstepActive(substep) {
      if (!this.currentSubstepKey) return false;
      return (substep.key ?? substep.label) === this.currentSubstepKey;
    },
    isSubstepCompleted(substep, index) {
      if (this.freeNavigation) return !!substep.completed;
      if (this.completed) return true;
      if (this.active && index < this.currentSubstepIndex) return true;
      return false;
    },
    isSubstepClickable(substep, index) {
      if (this.loading) return false;
      if (this.freeNavigation) {
        return !!substep.completed || (this.active && index <= this.currentSubstepIndex);
      }
      if (!this.clickable) return false;
      if (this.completed) return true;
      if (this.active && index < this.highestSubstepReached) return true;
      return false;
    },
    handleStepClick() {
      if (this.isRowClickable) this.$emit("click-step");
    },
    handleSubstepClick(substep, index) {
      if (this.isSubstepClickable(substep, index)) this.$emit("click-substep", index);
    },
  },
};
</script>

<style scoped>
.step-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid var(--v-grey-base);
  margin-left: 8px;
  flex: 1;
  min-width: 0;
}

.step-label-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.step-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.step-content.active {
  border-color: var(--v-systemPrimary-base);
}

.substeps {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.substep-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.substep-label {
  display: flex;
  align-items: center;
  padding: 5px 8px;
  border-radius: 4px;
  border: 1px solid var(--v-grey-base);
  flex: 1;
  min-width: 0;
}

.substep-label-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.substep-label.active {
  border-color: var(--v-systemPrimary-base);
}

.borderless {
  gap: 16px;
}

.borderless .step-content {
  border: none;
  border-radius: 0;
  margin: 0;
}

.sidebar-substeps {
  padding-left: 16px;
  gap: 16px;
}

/* Compact: hover fill is confined to the bordered containers */
.cursor-pointer:hover > .step-content {
  background-color: var(--v-lightGrey-base);
}
.substep-row.cursor-pointer:hover > .substep-label {
  background-color: var(--v-lightGrey-base);
}

/* Non-compact: hover fills the whole clickable top-level row */
.borderless > .cursor-pointer:hover {
  background-color: var(--v-lightGrey-base);
  border-radius: 4px;
}
</style>
